import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useDirtyGuard } from '@/hooks/useDirtyGuard';
import { useAppSelector } from '@/features/hooks';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { DraftRestoredBanner } from '@/components/common/DraftRestoredBanner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { FieldTooltip } from '@/components/ui/field-tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { ColorPicker } from '@/components/ui/color-picker';
import type { Category } from '@/lib/types';

const categorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(50),
  type: z.enum(['income', 'expense']),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/i, 'Use a hex color like #3b82f6')
    .default('#6b7280'),
  description: z.string().max(200, 'Description too long').optional().or(z.literal('')),
  emoji: z.string().optional(),
});

export type CategoryFormValues = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CategoryFormValues) => Promise<void>;
  category?: Category | null;
  isSubmitting?: boolean;
}

export function CategoryForm({ open, onOpenChange, onSubmit, category, isSubmitting }: CategoryFormProps) {
  const isEditing = !!category;
  const userId = useAppSelector((state) => state.auth.user?.id);

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      type: 'expense',
      color: '#6b7280',
      description: '',
      emoji: '',
    },
  });

  const { clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore } = useFormDraft<CategoryFormValues>(
    'category-create',
    userId,
    form,
    !isEditing && open
  );

  const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard, saveDraftAndClose } =
    useDirtyGuard(form, onOpenChange, clearDraft, saveDraftNow);

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
        type: category.type as CategoryFormValues['type'],
        color: category.color || '#6b7280',
        description: category.description || '',
        emoji: category.emoji || '',
      });
    } else {
      form.reset({ name: '', type: 'expense', color: '#6b7280', description: '', emoji: '' });
    }
  }, [category, form, open]);

  const handleSubmit = async (values: CategoryFormValues) => {
    await onSubmit(values);
    clearDraft();
    form.reset();
  };

  return (
    <>
    <Sheet open={open} onOpenChange={guardedOnOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Category' : 'Add Category'}</SheetTitle>
          <SheetDescription>
            {isEditing ? 'Update the category details below.' : 'Create a category to organize transactions.'}
          </SheetDescription>
        </SheetHeader>

        {justRestored && (
          <DraftRestoredBanner
            age={restoredAge}
            onDismiss={dismissRestore}
            className="mx-0 mt-4"
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name<RequiredAsterisk /><FieldTooltip text="A unique category name used in budgets and reports, e.g., 'Groceries', 'Entertainment', 'Utilities'." /></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Groceries" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color<OptionalTag /><FieldTooltip text="Assign a color for charts and visual distinction." /></FormLabel>
                  <FormControl>
                    <ColorPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emoji<OptionalTag /><FieldTooltip text="A visual tag for quick scanning. Purely decorative." /></FormLabel>
                  <FormControl>
                    <EmojiPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description<OptionalTag /><FieldTooltip text="Describe what this category covers. Helps when assigning transactions." /></FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add notes or guidance" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <Button variant="outline" type="button" onClick={() => guardedOnOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save changes' : 'Create category'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
      <DiscardChangesDialog
        open={showDiscard}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        onSaveDraft={!isEditing ? saveDraftAndClose : undefined}
      />
    </>
  );
}
